import React, { useEffect, useReducer, useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Autocomplete,
  Grid,
  Stack,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SendIcon from "@mui/icons-material/Send";
import PersonIcon from "@mui/icons-material/Person";
import ApartmentIcon from "@mui/icons-material/Apartment";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { MuiTelInput } from "mui-tel-input";
import { api } from "../../../config/config";
import { PhoneRegex } from "../../../components/Validation/PhoneRegex";
import { ToastContainer, toast } from "react-toastify";

// Vert de la marque SONABEL, repris du dégradé du bandeau de la page et des
// boutons de ContactItem (bg-green-700) — pour que ce formulaire s'accorde
// avec le reste de la page plutôt que d'utiliser le vert MUI par défaut.
const BRAND_GREEN = "#1e7e34";
const BRAND_GREEN_DARK = "#166a2c";

// Le catalogue (GET /type-reclamation) contient déjà un type "Autre" côté
// backend — repéré par libellé pour le renvoyer en bas de la liste et éviter
// de préremplir "objet" avec "Autre" (voir handleTypeChange plus bas).
const isAutreTypeLibelle = (libelle) => /^autres?$/i.test((libelle || "").trim());
const sortTypesAutreLast = (types) =>
  [...(types || [])].sort((a, b) => {
    const aAutre = isAutreTypeLibelle(a.libelle);
    const bAutre = isAutreTypeLibelle(b.libelle);
    if (aAutre === bAutre) return 0;
    return aAutre ? 1 : -1;
  });

const initialState = {
  nom: "",
  prenoms: "",
  telephone: "",
  email: "",
  ville_id: "",
  village_id: "",
  police: "",
  compteur: "",
  numeroAbonne: "",
  motif: "",
  typeReclamationId: "",
  objet: "",
  message: "",
};

function formReducer(state, action) {
  switch (action.type) {
    case "SET_FIELD":
      return { ...state, [action.field]: action.value };
    case "RESET_FORM":
      return initialState;
    default:
      return state;
  }
}

// Le conteneur parent (ContactPage) porte déjà l'ombre/le cadre général :
// chaque section est un bloc légèrement teinté (pas une carte à bordure
// pleine) pour rythmer le formulaire sans empiler les boîtes.
const FormSection = ({ title, icon, children }) => (
  <Box
    sx={{
      mb: 3,
      p: { xs: 2, sm: 2.5 },
      borderRadius: 2,
      bgcolor: (theme) => alpha(theme.palette.success.main, 0.04),
      border: "1px solid",
      borderColor: (theme) => alpha(theme.palette.success.main, 0.15),
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          bgcolor: BRAND_GREEN,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle1" fontWeight={700}>
        {title}
      </Typography>
    </Stack>
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Box>
);

function ContactUs() {
  const [state, dispatch] = useReducer(formReducer, initialState);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [typeOptions, setTypeOptions] = useState([]);
  // localite = ID numérique d'une localité (Avenant API Réclamations, section
  // 8 : "Entier positif, l'ID de localite" — seul champ de localisation
  // envoyé au serveur, qui en dérive exploitation/exploitationSecondaire/cd).
  // Même arborescence que le formulaire de demande de branchement pour la
  // choisir : Zone administrative (/ville/cd) puis Localité
  // (/village/ville/:id), cette dernière filtrée selon la zone.
  const [villes, setVilles] = useState([]);
  const [villages, setVillages] = useState([]);
  const [isLoadingVillages, setIsLoadingVillages] = useState(false);

  // Types de réclamation et zones : endpoints publics, pas d'authentification
  // requise.
  useEffect(() => {
    api
      .get("/type-reclamation", { params: { active: "true", perPage: 100 } })
      .then((response) => {
        setTypeOptions(response?.data?.content?.data || []);
      })
      .catch(() => setTypeOptions([]));

    api
      .get("/ville/cd")
      .then((response) => {
        const list = response?.data?.content || [];
        setVilles(list.map((v) => ({ id: v.id, name: (v.agence || "").trim() })));
      })
      .catch(() => setVilles([]));
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    dispatch({ type: "SET_FIELD", field: name, value });
  };

  // Police / numéro d'abonné : lettres et chiffres uniquement — on filtre à
  // la saisie plutôt que de valider après coup.
  const handleAlphanumericChange = (event) => {
    const { name, value } = event.target;
    dispatch({ type: "SET_FIELD", field: name, value: value.replace(/[^a-zA-Z0-9]+/g, "") });
  };

  const handleChangePhone = (value) => {
    dispatch({ type: "SET_FIELD", field: "telephone", value });
  };

  // Préremplit l'objet avec le libellé du type choisi ; l'utilisateur reste
  // libre de l'effacer et d'écrire autre chose ensuite. Exception : le type
  // "Autre" (déjà présent dans le catalogue backend, pas de champ dédié à
  // préremplir) — on vide l'objet pour que l'utilisateur y décrive
  // directement le type de sa réclamation.
  const handleTypeChange = (event, type) => {
    dispatch({ type: "SET_FIELD", field: "typeReclamationId", value: type?.id || "" });
    if (type) {
      dispatch({ type: "SET_FIELD", field: "objet", value: isAutreTypeLibelle(type.libelle) ? "" : type.libelle });
    }
  };

  const handleChangeVille = (event, value) => {
    dispatch({ type: "SET_FIELD", field: "ville_id", value: value?.id || "" });
    dispatch({ type: "SET_FIELD", field: "village_id", value: "" });
    setVillages([]);
    if (!value?.id) return;
    setIsLoadingVillages(true);
    api
      .get(`/village/ville/${value.id}`)
      .then((response) => {
        const list = response?.data?.content || [];
        setVillages(list.map((v) => ({ id: v.id, name: (v.localite || "").trim() })));
      })
      .catch(() => setVillages([]))
      .finally(() => setIsLoadingVillages(false));
  };

  const handleChangeVillage = (event, value) => {
    dispatch({ type: "SET_FIELD", field: "village_id", value: value?.id || "" });
  };

  const validateForm = () => {
    const formErrors = {};

    if (!state.nom.trim()) formErrors.nom = "Le nom est requis.";
    if (!state.prenoms.trim()) formErrors.prenoms = "Les prénoms sont requis.";
    if (!state.telephone.trim()) {
      formErrors.telephone = "Le numéro de téléphone est requis.";
    } else if (!PhoneRegex(state.telephone)) {
      formErrors.telephone = "Le numéro doit inclure l'indicatif du pays (ex. +226 70 00 00 00).";
    }
    if (state.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email.trim())) {
      formErrors.email = "L'email est invalide.";
    }
    if (!state.ville_id) {
      formErrors.ville = "Veuillez sélectionner votre zone administrative.";
    }
    if (!state.village_id) {
      formErrors.village = "Veuillez sélectionner votre localité.";
    }
    if (!state.objet.trim()) formErrors.objet = "L'objet est requis.";
    if (!state.message.trim()) formErrors.message = "La description de la réclamation est requise.";

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      toast.error("Veuillez corriger les champs en rouge avant de soumettre.");
      return;
    }

    setLoading(true);

    try {
      const dataToSend = {
        name: `${state.nom.trim()} ${state.prenoms.trim()}`.trim(),
        // Envoyé "collé" (sans les espaces insérés par MuiTelInput pendant
        // la saisie pour la lisibilité) — même format que côté interne.
        phone: state.telephone.replace(/\s+/g, ""),
        objet: state.objet.trim(),
        message: state.message.trim(),
        localite: Number(state.village_id),
        email: state.email.trim() || undefined,
        police: state.police.trim() || undefined,
        compteur: state.compteur.trim() || undefined,
        numeroAbonne: state.numeroAbonne.trim() || undefined,
        motif: state.motif.trim() || undefined,
        typeReclamationId: state.typeReclamationId || undefined,
      };

      const response = await api.post("/reclamation", dataToSend);

      if (response.status === 200 || response.status === 201) {
        toast.success("Votre réclamation a été enregistrée avec succès.");
        dispatch({ type: "RESET_FORM" });
        setErrors({});
      } else {
        toast.error("Une erreur est survenue. Veuillez réessayer.");
      }
    } catch (error) {
      const serverMessage = error?.response?.data?.content;
      toast.error(
        typeof serverMessage === "string"
          ? serverMessage
          : Array.isArray(serverMessage)
          ? serverMessage.map((e) => e.msg).join(", ")
          : "Une erreur est survenue. Veuillez réessayer."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: "100%", p: { xs: 1.5, sm: 3 } }}>
      <ToastContainer />
      <Box mb={3}>
        <Typography variant="h6" fontWeight={800}>
          Formulaire de réclamation
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Veuillez remplir les informations ci-dessous pour enregistrer votre réclamation. Les champs marqués d'un
          astérisque (*) sont obligatoires.
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <FormSection title="Vos coordonnées" icon={<PersonIcon fontSize="small" />}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Nom *"
              variant="outlined"
              name="nom"
              value={state.nom}
              onChange={handleChange}
              disabled={loading}
              error={!!errors.nom}
              helperText={errors.nom}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Prénoms *"
              variant="outlined"
              name="prenoms"
              value={state.prenoms}
              onChange={handleChange}
              disabled={loading}
              error={!!errors.prenoms}
              helperText={errors.prenoms}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MuiTelInput
              fullWidth
              label="Numéro de téléphone *"
              variant="outlined"
              defaultCountry="BF"
              forceCallingCode
              value={state.telephone}
              onChange={handleChangePhone}
              disabled={loading}
              error={!!errors.telephone}
              helperText={errors.telephone}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              variant="outlined"
              name="email"
              value={state.email}
              onChange={handleChange}
              disabled={loading}
              error={!!errors.email}
              helperText={errors.email}
            />
          </Grid>
        </FormSection>

        <FormSection title="Votre localisation" icon={<ApartmentIcon fontSize="small" />}>
          {/* Zone administrative puis Localité, dans cet ordre : la seconde
              ne se remplit qu'en fonction de la zone choisie dans la
              première (même arborescence que le formulaire de demande de
              branchement). */}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={villes}
              value={villes.find((v) => v.id === state.ville_id) || null}
              onChange={handleChangeVille}
              disabled={loading}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Zone administrative *"
                  placeholder="Recherchez votre zone"
                  error={!!errors.ville}
                  helperText={errors.ville}
                />
              )}
            />
          </Grid>
          {(isLoadingVillages || villages.length > 0) && (
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={villages}
                value={villages.find((v) => v.id === state.village_id) || null}
                onChange={handleChangeVillage}
                disabled={loading}
                loading={isLoadingVillages}
                getOptionLabel={(option) => option.name || ""}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Localité *"
                    placeholder="Recherchez votre localité"
                    error={!!errors.village}
                    helperText={errors.village}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingVillages ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          )}
        </FormSection>

        <FormSection title="Informations sur votre dossier (Facultatif)" icon={<AssignmentIcon fontSize="small" />}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Numéro de police"
              variant="outlined"
              name="police"
              placeholder="Ex: 1234567"
              value={state.police}
              onChange={handleAlphanumericChange}
              disabled={loading}
              error={!!errors.police}
              helperText={errors.police}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Numéro de compteur"
              variant="outlined"
              name="compteur"
              placeholder="Ex: AS535791"
              value={state.compteur}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Numéro d'abonné"
              variant="outlined"
              name="numeroAbonne"
              placeholder="Ex: 1234567"
              value={state.numeroAbonne}
              onChange={handleAlphanumericChange}
              disabled={loading}
            />
          </Grid>
        </FormSection>

        <FormSection title="Votre réclamation" icon={<ReportProblemIcon fontSize="small" />}>
          <Grid item xs={12}>
            <Autocomplete
              options={sortTypesAutreLast(typeOptions)}
              value={typeOptions.find((t) => t.id === state.typeReclamationId) || null}
              onChange={handleTypeChange}
              disabled={loading}
              getOptionLabel={(option) => option.libelle || ""}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Type de réclamation"
                  placeholder="Recherchez un type de réclamation"
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Objet *"
              placeholder="Résumez votre réclamation en quelques mots"
              variant="outlined"
              name="objet"
              value={state.objet}
              onChange={handleChange}
              disabled={loading}
              error={!!errors.objet}
              helperText={errors.objet}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description détaillée *"
              variant="outlined"
              name="message"
              value={state.message}
              onChange={handleChange}
              disabled={loading}
              multiline
              rows={4}
              error={!!errors.message}
              helperText={errors.message}
            />
          </Grid>
        </FormSection>

        <Button
          fullWidth
          variant="contained"
          type="submit"
          size="large"
          endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          disabled={loading}
          sx={{
            bgcolor: BRAND_GREEN,
            borderRadius: "999px",
            fontWeight: 700,
            py: 1.25,
            "&:hover": { bgcolor: BRAND_GREEN_DARK },
          }}
        >
          {loading ? "Enregistrement..." : "Enregistrer et soumettre"}
        </Button>
      </Box>
    </Box>
  );
}

export default ContactUs;
